import { useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslations } from 'next-intl';
import {
  default as RedirectionCard,
  Button as RedirectionCardButton,
  Title as RedirectionCardTitle,
} from '@monofront/design-system/components/RedirectionCard';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import Arrow from '@monofront/design-system/visuals/Icons/Arrow';
import { useFeatureIsOn } from '@monofront/business/contexts';
import {
  ABTEST_APPLE_PAY,
  FEATURE_FLAG_PAYPAL_SUBSCRIPTION,
} from '@monofront/business/constants';
import { PaymentMethodSource, PaymentMethodType } from '@monofront/types';
import { useSubscription } from '@monofront/business/hooks';
import PaypalProvider from '@context/PaypalProvider';
import IbanFormAbTest from '@components/PaymentForms/IbanForm/IbanFormAbTest';
import SubmitButton from '@components/PaymentForms/SubmitButton';
import { useAuthentication } from '@context/businessContainer';
import PaypalAgreementButton from '@components/PaymentForms/PaypalAgreementButton';
import SubscriptionCardForm from '@components/PaymentForms/SubscriptionCardForm';
import { trackEvent } from '@monofront/tracking';
import SubscribeWithApplePay from '@screens/CheckoutPayment/PaymentActions/SubscribeWithApplePay';

interface INewPaymentMethodListProps {
  setPaymentMethod: React.Dispatch<
    React.SetStateAction<PaymentMethodType | null>
  >;
  paymentMethod: PaymentMethodType | null;
  onUpdatePaymentMethodFinish: () => void;
}

const NewPaymentMethodList: React.FC<INewPaymentMethodListProps> = ({
  setPaymentMethod,
  paymentMethod,
  onUpdatePaymentMethodFinish,
}) => {
  const [isIbanFormCompleted, setIsIbanFormCompleted] = useState(false);
  const [isCardFormCompleted, setIsCardFormCompleted] = useState(false);
  const { isMember, authUser } = useAuthentication();
  const { updateSubscriptionPaymentMethod } = useSubscription(
    !!isMember,
    authUser?.email,
  );

  const tAccount = useTranslations('Account');
  const tCheckout = useTranslations('Checkout');
  const tManageSubs = useTranslations('Account.manageMySubscriptionSection');

  const isPaypalSubscriptionOn = useFeatureIsOn(
    FEATURE_FLAG_PAYPAL_SUBSCRIPTION,
  );
  const isApplePaySubscriptionOn = useFeatureIsOn(ABTEST_APPLE_PAY);

  const ErrorMessage = (
    <p>
      {tAccount.rich('paymentMethodUpdatedError', {
        strong: (children) => <strong>{children}</strong>,
      })}
    </p>
  );

  const handleSubscriptionPaymentUpdate = async (
    id: string,
    paymentMethodType: PaymentMethodType,
    paymentMethodSource?: PaymentMethodSource,
    financialPaymentId?: string,
  ) => {
    try {
      await updateSubscriptionPaymentMethod({
        paymentMethodId: id,
        paymentMethodSource,
        paymentMethodType,
        coupon: undefined,
        financialPaymentId,
      });

      trackEvent({
        type: 'LFPaymentMethodUpdated',
        data: {
          payment_method_action: 'added',
          update_source: 'account membership page',
        },
      });

      onUpdatePaymentMethodFinish();
    } catch (e) {
      toast.error(ErrorMessage);
    }
  };

  return (
    <PaypalProvider namespace="billing-agreement" options={{ vault: true }}>
      <div className="new-payment-method-list tw-mt-4">
        {!paymentMethod && (
          <div className="payment-methods">
            <RedirectionCard
              UNSAFE_className="tw-mb-4"
              variant="clear"
              withTitleOnly
              onClick={() => {
                setPaymentMethod(PaymentMethodType.CB);
              }}
            >
              <RedirectionCardTitle>
                <span className="tw-lf-body-regular-md tw-block">
                  {tManageSubs('updatePaymentCBTitle')}
                </span>
                <ResponsiveImage
                  alt="CB VISA Mastercard"
                  src="/images/checkout/payment-method.svg"
                  height={20}
                  width={108}
                />
              </RedirectionCardTitle>
              <RedirectionCardButton variant="clear">
                <Arrow width={14} height={14} headDirection="right" />
              </RedirectionCardButton>
            </RedirectionCard>
            {isPaypalSubscriptionOn && (
              <RedirectionCard
                UNSAFE_className="tw-mb-3"
                variant="clear"
                withTitleOnly
                onClick={() => {
                  setPaymentMethod(PaymentMethodType.PAYPAL);
                }}
              >
                <RedirectionCardTitle>
                  <span className="tw-lf-body-regular-md">
                    {tManageSubs('updatePaymentPaypalTitle')}
                  </span>
                  <span className="tw-relative tw-left-3 tw-top-[6px]">
                    <ResponsiveImage
                      alt="PayPal"
                      src="/images/checkout/paypal-brand.svg"
                      height={24}
                      width={90.75}
                      layout="fixed"
                      quality={100}
                    />
                  </span>
                </RedirectionCardTitle>
                <RedirectionCardButton variant="clear">
                  <Arrow width={14} height={14} headDirection="right" />
                </RedirectionCardButton>
              </RedirectionCard>
            )}
            <RedirectionCard
              UNSAFE_className="tw-mb-3"
              variant="clear"
              withTitleOnly
              onClick={() => {
                setPaymentMethod(PaymentMethodType.IBAN);
              }}
            >
              <RedirectionCardTitle>
                <span className="tw-lf-body-regular-md">
                  {tManageSubs('updatePaymentSEPATitle')}
                </span>
              </RedirectionCardTitle>
              <RedirectionCardButton variant="clear">
                <Arrow width={14} height={14} headDirection="right" />
              </RedirectionCardButton>
            </RedirectionCard>
            {isApplePaySubscriptionOn && (
              <SubscribeWithApplePay
                type="plain"
                onPaymentAuthorized={handleSubscriptionPaymentUpdate}
                height={72}
                radius={8}
                isMembershipCardUpdate
              />
            )}
          </div>
        )}

        {paymentMethod === PaymentMethodType.IBAN && (
          <IbanFormAbTest
            handleSubmitIban={handleSubscriptionPaymentUpdate}
            onComplete={(isValid) => {
              setIsIbanFormCompleted(isValid);
            }}
          >
            <p className="tw-lf-body-regular-sm tw-mt-4">
              {tManageSubs('sepaMessage')}
            </p>
            <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-border-0 tw-border-t tw-border-solid tw-border-blue-200 tw-bg-white tw-p-4">
              <SubmitButton
                buttonLabel={tManageSubs('addNewPaymentMethodCTA')}
                disabled={!isIbanFormCompleted}
                UNSAFE_className="tw-w-full"
              />
            </div>
          </IbanFormAbTest>
        )}
        {paymentMethod === PaymentMethodType.PAYPAL && (
          <>
            <div className="tw-max-w-[293px]">
              <PaypalAgreementButton
                autoInit
                onApprove={handleSubscriptionPaymentUpdate}
              />
            </div>
            <p className="tw-lf-body-regular-sm tw-mt-4">
              {tCheckout('paypalInfosAbTest')}
            </p>
          </>
        )}
        {paymentMethod === PaymentMethodType.CB && (
          <SubscriptionCardForm
            handleSubmitCard={handleSubscriptionPaymentUpdate}
            onComplete={(isValid) => {
              setIsCardFormCompleted(isValid);
            }}
          >
            <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-border-0 tw-border-t tw-border-solid tw-border-blue-200 tw-bg-white tw-p-4">
              <SubmitButton
                buttonLabel={tManageSubs('addNewPaymentMethodCTA')}
                disabled={!isCardFormCompleted}
                UNSAFE_className="tw-w-full"
              />
            </div>
          </SubscriptionCardForm>
        )}
      </div>
    </PaypalProvider>
  );
};

export default NewPaymentMethodList;
